import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { Logo } from '@retainerclub/common';
import { useAuth } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import DesktopNavAuthed from './DesktopNav/DesktopNavAuthed';
import DesktopNavCart from './DesktopNav/DesktopNavCart';
import DesktopNavProfile from './DesktopNav/DesktopNavProfile';
import DesktopNavUnauthed from './DesktopNav/DesktopNavUnauthed';
import MobileNavAuthed from './MobileNav/MobileNavAuthed';
import MobileNavProfile from './MobileNav/MobileNavProfile';
import MobileNavUnauthed from './MobileNav/MobileNavUnauthed';

function Header({ className = '' }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { currentUser } = useAuth();

  const app = import.meta.env.VITE_APP_NAME;

  const location = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: needed to check for pathname changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`shrink-0 border-black/10 border-b bg-white ${className}`}
    >
      <nav
        className="flex items-center justify-between px-6 py-4 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link to="/">
            <span className="sr-only">Retainer Club</span>
            <Logo color="black" width={122} height={35} />
          </Link>
        </div>
        <div className="flex gap-x-4">
          {app === 'rc_customer' && (
            <div className="lg:hidden">
              <DesktopNavCart />
            </div>
          )}
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="size-6" aria-hidden="true" />
          </button>
        </div>

        <div className="hidden lg:flex lg:gap-x-12">
          {currentUser ? <DesktopNavAuthed /> : <DesktopNavUnauthed />}
        </div>
        <div className="hidden items-center gap-x-4 lg:flex lg:flex-1 lg:justify-end">
          {app === 'rc_customer' ? (
            <>
              <DesktopNavCart />
              <DesktopNavProfile />
            </>
          ) : (
            <a
              href={`${import.meta.env.VITE_CUSTOMER_APP}/signin`}
              className="flex items-center font-semibold text-gray-900 text-sm leading-6"
            >
              <UserCircleIcon className="mr-2 size-8" aria-hidden="true" />
              <span className="font-medium text-base text-rcprimary-400">
                Sign In
              </span>
            </a>
          )}
        </div>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link
              to={currentUser ? '/' : 'https://retainerclub.com'}
              className="-m-1.5 p-1.5"
            >
              <span className="sr-only">Retainer Club</span>
              <Logo color="black" />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="size-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div>
              <div className="space-y-1">
                {currentUser ? <MobileNavAuthed /> : <MobileNavUnauthed />}

                <MobileNavProfile />
              </div>
              <div>
                {app !== 'rc_customer' && (
                  <a
                    href={`${import.meta.env.VITE_CUSTOMER_APP}/signin`}
                    className="flex items-center font-semibold text-gray-900 text-sm leading-6"
                  >
                    <UserCircleIcon
                      className="mr-2 size-8"
                      aria-hidden="true"
                    />
                    <span className="font-normal text-base text-rcprimary-400">
                      Sign In
                    </span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}

Header.propTypes = {
  className: PropTypes.string,
};

export default Header;
